import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { apiSlice } from "../../app/api/apiSlice";
import {
  useChangeAdminPasswordMutation,
  useSyncStripeWebhooksMutation,
} from "../../features/api/adminApiSlice"; // Import hook
import "./adminSettings.scss";

export default function AdminSettings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [syncStripeWebhooks, { isLoading: isSyncingStripe }] =
    useSyncStripeWebhooksMutation();

  // Toast alert states
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Settings page configurations states
  const [enableGpsTracking, setEnableGpsTracking] = useState(true);
  const [mapStyleTheme, setMapStyleTheme] = useState("satellite");

  // 🔑 NEW: Local Password Input States
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [changeAdminPassword, { isLoading: isUpdatingPassword }] =
    useChangeAdminPasswordMutation();

  const showNotification = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3500
    );
  };

  const handleClearCache = () => {
    try {
      dispatch(apiSlice.util.resetApiState());
      showNotification(
        "🧹 Redux Toolkit cache cleared! All data feeds refetched safely.",
        "success"
      );
    } catch (err) {
      showNotification("⚠️ Unable to flush local state cache tables.", "error");
    }
  };

  // 🔑 NEW: Handle Submit for Password Changes
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      showNotification(
        "⚠️ Please fill out all required password rows.",
        "error"
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      showNotification("⚠️ New passwords do not match.", "error");
      return;
    }

    try {
      await changeAdminPassword({ currentPassword, newPassword }).unwrap();
      showNotification("🔒 Password changed successfully!", "success");

      // Clear password form fields on completion
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      showNotification(
        err?.data?.message || "Failed to modify security credentials.",
        "error"
      );
    }
  };

  // 🕒 15-Minute Auto-Logout Watchdog Engine
  const handleLogoutAction = useCallback(() => {
    localStorage.removeItem("adminToken");
    sessionStorage.removeItem("activeAdminSession");
    showNotification(
      "🔒 Session expired due to inactivity. Logging out...",
      "error"
    );
    setTimeout(() => navigate("/login"), 1500);
  }, [navigate]);

  useEffect(() => {
    const activeTimeoutMs = 15 * 60 * 1000;
    let idleTimer;
    const resetIdleTimeoutCountdown = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(handleLogoutAction, activeTimeoutMs);
    };
    const interactionEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];
    interactionEvents.forEach((evt) =>
      window.addEventListener(evt, resetIdleTimeoutCountdown)
    );
    resetIdleTimeoutCountdown();
    return () => {
      clearTimeout(idleTimer);
      interactionEvents.forEach((evt) =>
        window.removeEventListener(evt, resetIdleTimeoutCountdown)
      );
    };
  }, [handleLogoutAction]);

  const handleStripeSync = async () => {
    try {
      await syncStripeWebhooks().unwrap();
      showNotification(
        "💳 Stripe transaction logs and webhook connection pipelines verified!",
        "success"
      );
    } catch (err) {
      showNotification(
        "⚠️ Failed to verify Stripe webhook handshakes.",
        "error"
      );
    }
  };

  return (
    <div className="admin-settings-page fuel-fade-in">
      {toast.show && (
        <div className={`custom-toast custom-toast--${toast.type}`}>
          <span className="toast-icon">
            {toast.type === "success" ? "✨" : "⚠️"}
          </span>
          <p>{toast.message}</p>
        </div>
      )}

      <div className="settings-header">
        <h2>System Parameters & Utilities</h2>
        <p>
          Maintain your data tracking layers, reset local cache storage data
          tables, or toggle security credentials.
        </p>
      </div>

      <div className="settings-grid">
        {/* WIDGET CARD 1: CACHE SYSTEM UTILITIES */}
        <section className="settings-card">
          <div className="settings-card__title">
            <h3>🔧 Cache Management Utilities</h3>
          </div>
          <div className="settings-card__body">
            <p>
              If you just modified items directly inside your database panel (or
              replaced images directly on AWS S3 buckets) and the website is
              still rendering older cached results, click below to force-refresh
              all data.
            </p>
            <button
              type="button"
              className="delete-action-btn purge-cache-btn"
              onClick={handleClearCache}
            >
              🧹 Flush Central Frontend Caches
            </button>
          </div>
        </section>

        {/* 🔑 NEW WIDGET CARD 2: PASSWORD CREDENTIALS EDITOR SECTION */}
        <section className="settings-card">
          <div className="settings-card__title">
            <h3>🔒 Update Security Password</h3>
          </div>
          <div className="settings-card__body">
            <form
              onSubmit={handlePasswordSubmit}
              className="settings-form-stack"
            >
              <label className="settings-input-label">
                Current Security Password
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </label>
              <label className="settings-input-label">
                New Security Password
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                />
              </label>
              <label className="settings-input-label">
                Confirm New Password
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm characters"
                />
              </label>
              <button
                type="submit"
                className="save-settings-btn"
                disabled={isUpdatingPassword}
              >
                {isUpdatingPassword
                  ? "Updating Account..."
                  : "Modify Account Password"}
              </button>
            </form>
          </div>
        </section>

        {/* WIDGET CARD 3: TRAVEL ROUTE MAP SETTINGS PANEL */}
        <section className="settings-card">
          <div className="settings-card__title">
            <h3>🗺️ Live Route Map Parameters</h3>
          </div>
          <div className="settings-card__body settings-form-stack">
            <p>
              Control how your small-car GPS map layers render for your public
              website audience stream.
            </p>

            <label className="settings-toggle-row">
              <input
                type="checkbox"
                checked={enableGpsTracking}
                onChange={(e) => setEnableGpsTracking(e.target.checked)}
              />
              <div className="toggle-label-text">
                <strong>Enable Public GPS Coordinates</strong>
                <span>
                  Broadcast car position markers on the live route tracking
                  feed.
                </span>
              </div>
            </label>

            <label className="settings-select-row">
              <div className="select-label-text">
                <strong>Default Map Canvas Theme</strong>
                <span>Choose base display graphics look for map paths.</span>
              </div>
              <select
                value={mapStyleTheme}
                onChange={(e) => setMapStyleTheme(e.target.value)}
              >
                <option value="satellite">
                  🛰️ Satellite Cinematic Imagery
                </option>
                <option value="terrain">⛰️ Topographic Terrain Layout</option>
                <option value="dark-minimal">
                  🌑 High-Contrast Dark Canvas
                </option>
              </select>
            </label>
          </div>
        </section>

        {/* WIDGET CARD 4: SECURITY ACTIVITY INFO */}
        <section className="settings-card">
          <div className="settings-card__title">
            <h3>🛡️ Session Security Automation</h3>
          </div>
          <div className="settings-card__body">
            <p>
              To protect your travel logs and database keys, this platform uses
              an automated security watchdog loop.
            </p>
            <div className="security-status-box">
              <span className="pulse-heartbeat"></span>
              <p>
                <strong>Auto-Logout Guard Active:</strong> Your admin session
                will automatically terminate if no movement coordinates are
                logged for <strong>15 minutes</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* STRIPE */}
        <section className="settings-card">
          <div className="settings-card__title">
            <h3>💳 Stripe Infrastructure Utilities</h3>
          </div>
          <div className="settings-card__body settings-form-stack">
            <p>
              Monitor your incoming transaction pipelines, test communication
              handshakes, or manually sync stuck webhooks.
            </p>

            <div
              className="env-row"
              style={{
                paddingBottom: "1rem",
                borderBottom: "1px solid rgba(255,255,255,0.03)",
              }}
            >
              <strong>Webhook Handshake:</strong>{" "}
              <span style={{ color: "#4ade80" }}>🟢 Active Listening</span>
            </div>

            <button
              type="button"
              className="save-settings-btn"
              onClick={handleStripeSync}
              disabled={isSyncingStripe}
            >
              {isSyncingStripe
                ? "Pinging Stripe Core..."
                : "🔄 Audit Webhook Sync Pipelines"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
